import React from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useLazyGetCustomersQuery } from "../../masters/customers/customersApi";

const toOption = (c) => ({
  value: c.custId,
  label: `${c.custName} (${c.branchCode || c.custId})`,
  customer: c,
});

// "Search Customer Master" picker used everywhere a Stock document needs a
// party (LPO/Stock-In/Stock-Return supplier, Stock-Out's customer recipient)
// - there's no separate Supplier master in this system, per direct
// instruction everything comes from Customer Master.
//
// mode="all-branches" (every real call site today) searches every branch
// the CALLER is actually allowed to see: GET /masters/customers with no
// branchId lets the server default to the caller's own full branch set
// (BranchFilterHelper.Resolve), and sp_master_customer_list already unions
// in anything explicitly shared to one of those branches via the "Also
// Show This Customer At" branch map (Batch09_BranchMapping.sql) - so a
// single-branch user only ever sees their own customers plus whatever's
// been deliberately shared to them, never every branch's customer list.
//
// 2026-08-24 fix: this used to call /customers/search-all-branches, which
// the API itself documents as "deliberately not branch-scoped" - that
// endpoint exists for the cross-branch duplicate-check shown while
// creating a NEW customer (still used there, and in LinkedCustomersPanel),
// never meant to gate which customers a live transaction can pick from.
// Reusing it here silently bypassed both the caller's own branch
// restriction and the branch-map sharing control entirely.
//
// mode="branch" restricts the search to one `branchId` - kept as a general
// capability but unused by any current caller now that every document has
// a proper branch pairing.
//
// `creatable` (LPO/Stock-In/Stock-Return supplier only, never Stock-Out)
// lets the operator type a name that isn't in Customer Master at all and
// use it as-is. The resulting option has customer: null - callers must
// treat that as "no linked custId", not an error.
const PartyPicker = ({ mode = "all-branches", branchId, value, onChange, disabled, creatable = false }) => {
  const [triggerSearch] = useLazyGetCustomersQuery();

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length === 0) return [];
    if (mode === "all-branches") {
      // isActive: true - a live transaction (LPO/Stock-In/Stock-Return/Stock-Out) should never
      // offer an inactive customer/supplier as a pickable party. Missing before, so this search
      // silently mixed inactive customers in with active ones.
      const { data = [] } = await triggerSearch({ search: inputValue.trim(), isActive: true });
      return data.map(toOption);
    }
    if (!branchId) return [];
    const { data = [] } = await triggerSearch({ branchId, search: inputValue.trim(), isActive: true });
    return data.map(toOption);
  };

  const isReady = mode === "all-branches" || Boolean(branchId);
  const Select = creatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <Select
      classNamePrefix="react-select"
      cacheOptions
      loadOptions={loadOptions}
      value={value}
      onChange={(opt) =>
        onChange(opt && opt.__isNew__ ? { value: null, label: opt.label, customer: null } : opt)
      }
      isDisabled={disabled || !isReady}
      isClearable
      placeholder={isReady ? "Search Customer Master" : "Select a branch first"}
      formatCreateLabel={(input) => `Use "${input}" (not in Customer Master)`}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching customers" : "Type to search Customer Master"
      }
      // Same menu-portal fix as ProductPicker.jsx - avoids the dropdown
      // being clipped by the line-items table/form panel it sits inside.
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  );
};

export default PartyPicker;
